import { BootstrapOptions, Component, OnDestroy, OnInit } from '@angular/core';
import { rosterProfileIconUrl } from '../../utils/roster-image';
import { RosterService } from '../../services/roster.service';
import { Roster } from '../../models/roster';
import { CommonModule } from '@angular/common';
import { DialogComponent } from "../dialog/dialog.component";
import { FormsModule } from '@angular/forms';
import { YesNoPipe } from '../../pipes/yesNoPipe';
import { ProfileImagePipe } from '../../pipes/profileImagePipe';
import { RouterModule } from '@angular/router';
import { UserService } from '../../services/user.service';
import { User, UserRole } from '../../models/user';
import { CampaignService } from '../../services/campaign.service';
import { Campaign } from '../../models/campaign';
import { genreColorClass } from '../../utils/genre-colors';

@Component({
  selector: 'app-rosters',
  imports: [CommonModule, DialogComponent, FormsModule, YesNoPipe, ProfileImagePipe, RouterModule],
  templateUrl: './rosters.component.html',
  styleUrl: './rosters.component.css'
})
export class RostersComponent implements OnInit, OnDestroy {

  // Phone layout: the grid table is replaced by one tile per roster.
  static readonly SmallScreenQuery = '(max-width: 768px)';
  isSmallScreen: boolean = false;
  mobileFiltersOpen: boolean = false;
  private smallScreenMediaQuery?: MediaQueryList;
  private onSmallScreenChange = (e: MediaQueryListEvent | MediaQueryList) => { this.isSmallScreen = e.matches; };

  genreColorClass = genreColorClass;

  rosters: Roster[] = [];

  selectedIds: Set<string> = new Set();

  private _campaignFilter: string = '';
  get campaignFilter(): string { return this._campaignFilter; }
  set campaignFilter(value: string) {
    this._campaignFilter = value;
    sessionStorage.setItem('vtg_roster_campaign', value);
  }

  toggleSelection(id: string): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
    sessionStorage.setItem('vtg_roster_selectedIds', [...this.selectedIds].join(','));
  }

  get selectedIdsParam(): string {
    return [...this.selectedIds].join(',');
  }

  showAddRosterDialog: boolean = false;

  loading: boolean = true;
  saving: boolean = false;

  profileIconUrl = rosterProfileIconUrl;

  private _activeFilter: string = 'both';
  get activeFilter(): string { return this._activeFilter; }
  set activeFilter(v: string) { this._activeFilter = v; try { localStorage.setItem('rosters.activeFilter', v); } catch (e) {} }

  private _featuredFilter: string = 'both';
  get featuredFilter(): string { return this._featuredFilter; }
  set featuredFilter(v: string) { this._featuredFilter = v; try { localStorage.setItem('rosters.featuredFilter', v); } catch (e) {} }

  private _exclusiveFilter: string = 'both';
  get exclusiveFilter(): string { return this._exclusiveFilter; }
  set exclusiveFilter(v: string) { this._exclusiveFilter = v; try { localStorage.setItem('rosters.exclusiveFilter', v); } catch (e) {} }

  private _genreFilter: string = 'all';
  get genreFilter(): string { return this._genreFilter; }
  set genreFilter(v: string) { this._genreFilter = v; try { localStorage.setItem('rosters.genreFilter', v); } catch (e) {} }

  private _nameFilter: string = '';
  get nameFilter(): string { return this._nameFilter; }
  set nameFilter(v: string) { this._nameFilter = v; try { localStorage.setItem('rosters.nameFilter', v); } catch (e) {} }

  private readonly rateFieldNames: string[] = [
    'tiktokVideoBaseRate', 'tiktokVideoMinRate',
    'instagramReelBaseRate', 'instagramReelMinRate',
    'instagramStory3FramesRate', 'instagramStory3FramesMinRate',
    'instagramStorySwipeLinkRate', 'instagramStorySwipeLinkMinRate',
    'instagramCarouselBaseRate', 'instagramCarouselMinRate',
    'youtubeIntegrationRate', 'youtubeIntegrationMinRate',
    'youtubeDedicatedVideoRate', 'youtubeDedicatedVideoMinRate',
    'youtubeShortBaseRate', 'youtubeShortMinRate',
    'ugcPerVideoBaseRate',
  ];

  ratesStatus(roster: Roster): 'none' | 'all' | 'some' | 'complete' {
    if (!roster.exclusive || !roster.active) return 'none';
    const r = roster as unknown as Record<string, number | null | undefined>;
    const missing = this.rateFieldNames.filter(f => !r[f]).length;
    if (missing === 0) return 'complete';
    if (missing === this.rateFieldNames.length) return 'all';
    return 'some';
  }

  isRosterVisible(roster: Roster): boolean {
    if (this.exclusiveFilter === 'yes' && !roster.exclusive) return false;
    if (this.exclusiveFilter === 'no' && roster.exclusive) return false;
    if (this.activeFilter === 'yes' && !roster.active) return false;
    if (this.activeFilter === 'no' && roster.active) return false;
    if (this.featuredFilter === 'yes' && !roster.featured) return false;
    if (this.featuredFilter === 'no' && roster.featured) return false;
    if (this.genreFilter !== 'all') {
      const lines = (roster.genre || '').split('\n').map(g => g.trim());
      if (!lines.includes(this.genreFilter)) return false;
    }
    if (this.nameFilter) {
      const q = this.nameFilter.toLowerCase();
      const matchesName = roster.name.toLowerCase().includes(q);
      const matchesHandle = [roster.tiktokHandle, roster.instagramHandle, roster.youtubeHandle]
        .some(h => h && h.toLowerCase().includes(q));
      if (!matchesName && !matchesHandle) return false;
    }
    return true;
  }

  newRosterModel!: Roster;

  get genres(): string[] {
    const all = new Set<string>();
    for (const r of this.rosters) {
      if (r.genre) {
        r.genre.split('\n').map(g => g.trim()).filter(g => g).forEach(g => all.add(g));
      }
    }
    return [...all].sort();
  }

  genreTags(roster: Roster): string[] {
    return (roster.genre || '').split('\n').map(g => g.trim()).filter(g => g);
  }

  get visibleRosterCount(): number {
    return this.rosters.filter(r => this.isRosterVisible(r)).length;
  }

  toggleMobileFilters(): void {
    this.mobileFiltersOpen = !this.mobileFiltersOpen;
  }



  agentUsers: User[] = [];
  sourceUsers: User[] = [];

  // Heat indicator: maps roster id -> heat level.
  // Positive (1..3) = flames (recent campaigns), negative (-1..-3) = ice cubes (cold streak).
  private heatByRosterId = new Map<string, number>();
  private heatReady = false;

  constructor(
    private rosterService: RosterService,
    private userService: UserService,
    private campaignService: CampaignService
  ) {}

  private computeHeatLevels(campaigns: Campaign[]): void {
    const now = new Date();
    const m1 = new Date(now); m1.setMonth(now.getMonth() - 1);
    const m2 = new Date(now); m2.setMonth(now.getMonth() - 2);
    const m3 = new Date(now); m3.setMonth(now.getMonth() - 3);

    const datesByCreator = new Map<string, Date[]>();
    for (const c of campaigns) {
      if (c.deleted || !c.creatorId || !c.createdDate) continue;
      const d = new Date(c.createdDate);
      if (isNaN(d.getTime())) continue;
      const arr = datesByCreator.get(c.creatorId);
      if (arr) arr.push(d); else datesByCreator.set(c.creatorId, [d]);
    }

    this.heatByRosterId.clear();
    for (const [creatorId, dates] of datesByCreator) {
      const countLastMonth = dates.filter(d => d >= m1).length;
      let heat: number;
      if (countLastMonth >= 3) heat = 3;
      else if (countLastMonth === 2) heat = 2;
      else if (countLastMonth === 1) heat = 1;
      else {
        const mostRecent = dates.reduce((a, b) => (a > b ? a : b));
        if (mostRecent >= m2) heat = -1;
        else if (mostRecent >= m3) heat = -2;
        else heat = -3;
      }
      this.heatByRosterId.set(creatorId, heat);
    }
    this.heatReady = true;
  }

  // Rosters with no campaigns at all default to -3 (no campaigns in the last 3 months).
  // Returns 0 (renders nothing) until campaign data has loaded.
  heatLevel(roster: Roster): number {
    if (!this.heatReady) return 0;
    return this.heatByRosterId.get(roster.id) ?? -3;
  }

  range(n: number): number[] {
    return Array.from({ length: Math.max(0, n) });
  }

  heatTooltip(roster: Roster): string {
    const h = this.heatLevel(roster);
    if (h === 3) return '3+ campaigns in the last month';
    if (h > 0) return `${h} campaign${h === 1 ? '' : 's'} in the last month`;
    if (h === -1) return 'No campaigns in the last month';
    if (h === -2) return 'No campaigns in the last 2 months';
    return 'No campaigns in the last 3 months';
  }

  ngOnDestroy(): void {
    this.smallScreenMediaQuery?.removeEventListener('change', this.onSmallScreenChange);
  }

  ngOnInit(): void {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.smallScreenMediaQuery = window.matchMedia(RostersComponent.SmallScreenQuery);
      this.isSmallScreen = this.smallScreenMediaQuery.matches;
      this.smallScreenMediaQuery.addEventListener('change', this.onSmallScreenChange);
    }

    this.userService.getUsers().subscribe(users => {
      this.agentUsers = users.filter(u => u.role === UserRole.Agent || u.role === UserRole.Scouting || u.role === UserRole.Casting || u.role === UserRole.Admin);
      this.sourceUsers = users.filter(u => u.role === UserRole.Scouting);
    });
    const savedIds = sessionStorage.getItem('vtg_roster_selectedIds') ?? '';
    this.selectedIds = new Set(savedIds.split(',').filter(id => id.length > 0));
    this._campaignFilter = sessionStorage.getItem('vtg_roster_campaign') ?? '';

    try {
      const nf = localStorage.getItem('rosters.nameFilter');
      const af = localStorage.getItem('rosters.activeFilter');
      const ff = localStorage.getItem('rosters.featuredFilter');
      const ef = localStorage.getItem('rosters.exclusiveFilter');
      const gf = localStorage.getItem('rosters.genreFilter');
      if (nf !== null) this._nameFilter = nf;
      if (af !== null) this._activeFilter = af;
      if (ff !== null) this._featuredFilter = ff;
      if (ef !== null) this._exclusiveFilter = ef;
      if (gf !== null) this._genreFilter = gf;
    } catch (e) { /* ignore storage errors */ }

    this.campaignService.getCampaigns().subscribe({
      next: (campaigns) => { this.computeHeatLevels(campaigns); },
      error: (err) => { console.log(err); }
    });

    this.loading = true;
    this.rosterService.getRosters().subscribe({
      next: (result) => {
        this.rosters = result.sort((a, b) => a.name.localeCompare(b.name));
      },
      error: (err) => {
        console.log(err);
      },
      complete: () => {
        this.loading = false;
      }
    });

    // Server understands empty guid and will replace on post.
    this.newRosterModel = {
      id: '00000000-0000-0000-0000-000000000000',
      name: '',
      active: true,
      featured: false,
      exclusive: true,
      featureRank: 0,
      phone: '',
      genre: 'Food',
      notes: '',
      signDate: '',
      contractExpireDate: '',
      tiktokHandle: '',
      instagramHandle: '',
      youtubeHandle: '',
      imageUrl: '',
      tileImageUrl: '',
      verticalEmail: '',
      inboundCommission: 0,
      outboundCommission: 0,
      externalEmail: '',
      agent: '',
      coordinator: '',
      source: '',
      agentEmail: '',
      llcName: '',
      shippingAddress: '',
      city: '',
      state: '',
      birthday: '',
      gender: '',
      brandExclusions: '',
      exclusivities: [],
      tiktokVideoBaseRate: 0,
      tiktokVideoMinRate: 0,
      instagramReelBaseRate: 0,
      instagramReelMinRate: 0,
      instagramStory3FramesRate: 0,
      instagramStory3FramesMinRate: 0,
      instagramStorySwipeLinkRate: 0,
      instagramStorySwipeLinkMinRate: 0,
      instagramCarouselBaseRate: 0,
      instagramCarouselMinRate: 0,
      youtubeIntegrationRate: 0,
      youtubeIntegrationMinRate: 0,
      youtubeDedicatedVideoRate: 0,
      youtubeDedicatedVideoMinRate: 0,
      youtubeShortBaseRate: 0,
      youtubeShortMinRate: 0,
      ugcPerVideoBaseRate: 0,
      pastPartnerships: '',
      attributes: ''
    };
  }

  toDateInput(dateStr: string): string {
    if (!dateStr) return '';
    return dateStr.substring(0, 10);
  }

  onSignDateChange(roster: Roster, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.rosterService.patchRosterField(roster.id, 'signDate', value || null).subscribe({
      next: () => { roster.signDate = value; },
      error: (err) => { console.log(err); }
    });
  }

  onGenreChange(roster: Roster, value: string): void {
    this.rosterService.patchRosterField(roster.id, 'genre', value).subscribe({
      next: () => { roster.genre = value; },
      error: (err) => { console.log(err); }
    });
  }

  onNotesChange(roster: Roster, value: string): void {
    this.rosterService.patchRosterField(roster.id, 'notes', value).subscribe({
      next: () => { roster.notes = value; },
      error: (err) => { console.log(err); }
    });
  }

  onCommissionChange(roster: Roster, field: 'inboundCommission' | 'outboundCommission', event: Event): void {
    const value = parseInt((event.target as HTMLInputElement).value, 10);
    this.rosterService.patchRosterField(roster.id, field, isNaN(value) ? null : value).subscribe({
      next: () => { roster[field] = value; },
      error: (err) => { console.log(err); }
    });
  }

  toggleBooleanField(roster: Roster, field: 'active' | 'featured' | 'exclusive'): void {
    const newValue = !roster[field];
    this.rosterService.patchRosterField(roster.id, field, newValue).subscribe({
      next: () => { roster[field] = newValue; },
      error: (err) => { console.log(err); }
    });
  }

  get canAddToRoster(): boolean {
    if (!this.newRosterModel?.name) return false;
    if (!this.newRosterModel.agent) return false;
    if (!this.newRosterModel.coordinator) return false;
    if (!this.newRosterModel.gender) return false;
    if (!this.newRosterModel.exclusive) return true;
    return !!this.newRosterModel.verticalEmail;
  }

  ShowAddToRosterDialog() {
    this.showAddRosterDialog = true;
  }

  AddToRosterClick() {
    this.saving = true;
    this.showAddRosterDialog = false;

    this.rosterService.upsertRoster(this.newRosterModel).subscribe({
      next: (result) => {
        this.rosters.push(result);
      },
      error: (err) => {
        console.log(err);
      },
      complete: () => {
        this.saving = false;
      }
    })
  }

}
